import React, { useState, useEffect } from 'react';
import { FiX, FiCalendar, FiUser, FiFileText, FiSave, FiEdit2 } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EditBookingModal = ({ 
  showEditModal, 
  setShowEditModal,
  bookingId 
}) => {
  const [formData, setFormData] = useState({
    checkinDate: '',
    checkinTime: '',
    expectedCheckoutDate: '',
    expectedCheckoutTime: '',
    roomAmount: '',
    paidAmount: '',
    specialRequests: '',
    notes: '',
    persons: []
  });
  const [rooms, setRooms] = useState([]);
  const [floors, setFloors] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('');

  // Fetch booking data when modal opens
  useEffect(() => {
    if (showEditModal && bookingId) {
      fetchBookingData();
    }
  }, [showEditModal, bookingId]);

  const fetchBookingData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendor/checkin-management/checkin-by-id/${bookingId}`);
      const booking = response.data;
      
      const checkinDateTime = new Date(booking.checkinDateTime);
      const expectedCheckoutDateTime = new Date(booking.expectedCheckoutDateTime);
      
      setFormData({
        roomId: booking.roomId?._id || '',
        checkinDate: checkinDateTime.toISOString().split('T')[0],
        checkinTime: checkinDateTime.toTimeString().slice(0, 5),
        expectedCheckoutDate: expectedCheckoutDateTime.toISOString().split('T')[0],
        expectedCheckoutTime: expectedCheckoutDateTime.toTimeString().slice(0, 5),
        roomAmount: booking.pricing?.initialRoomAmount || '',
        paidAmount: booking.pricing?.paidAmount || '',
        paymentMethod: booking.paymentMethod || 'cash',
        specialRequests: booking.specialRequests || '',
        notes: booking.notes || '',
        persons: booking.persons || []
      });
      setSelectedFloor(booking?.floorId?._id || '');
      setBookingStatus(booking.status || '');
    } catch (error) {
      console.error('Error fetching booking data:', error);
      toast.error('Failed to load booking data');
      setShowEditModal(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch floors and rooms
  useEffect(() => {
    const fetchFloorsAndRooms = async () => {
      try {
        const [floorsRes, roomsRes] = await Promise.all([
          api.get('/vendor/room-management/floors'),
          api.get('/vendor/room-management/rooms?isPaginate=false')
        ]);

        setFloors((floorsRes?.data || []));
        setRooms(roomsRes?.data?.rooms || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load floors and rooms');
        // Set empty arrays on error to prevent map errors
        setFloors([]);
        setRooms([]);
      }
    };
    fetchFloorsAndRooms();
  }, []);

  // Filter rooms when floor changes
  useEffect(() => {
    // Ensure rooms is an array before filtering
    const roomsArray = Array.isArray(rooms) ? rooms : [];
    
    if (selectedFloor) {
      const filtered = roomsArray.filter(room => 
        room.floorId?._id === selectedFloor
      );
      setFilteredRooms(filtered);
    } else {
      setFilteredRooms(roomsArray);
    }
  }, [selectedFloor, rooms]);

  const handleFloorChange = (floorId) => {
    setSelectedFloor(floorId);
    setFormData({ ...formData, roomId: '' });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Calculate remaining amount when room amount or paid amount changes
    if (field === 'roomAmount' || field === 'paidAmount') {
      const roomAmount = field === 'roomAmount' ? parseFloat(value) || 0 : parseFloat(formData.roomAmount) || 0;
      const paidAmount = field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(formData.paidAmount) || 0;
      const remainingAmount = Math.max(0, roomAmount - paidAmount);
      setFormData(prev => ({ ...prev, remainingAmount: remainingAmount.toString() }));
    }
  };

  const updatePerson = (index, field, value) => {
    const updatedPersons = [...formData.persons];
    updatedPersons[index] = { ...updatedPersons[index], [field]: value };
    setFormData({ ...formData, persons: updatedPersons });
  };

  const addPerson = () => {
    setFormData({
      ...formData,
      persons: [...formData.persons, {
        name: '',
        mobileNumber: '',
        email: '',
        identityNumber: '',
        age: '',
        gender: 'male',
        address: ''
      }]
    });
  };

  const removePerson = (index) => {
    const updatedPersons = formData.persons.filter((_, i) => i !== index);
    setFormData({ ...formData, persons: updatedPersons });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bookingId) return;

    setLoading(true);
    try {
      const requestData = {
        roomId: formData.roomId,
        checkinDateTime: new Date(`${formData.checkinDate}T${formData.checkinTime}`),
        expectedCheckoutDateTime: new Date(`${formData.expectedCheckoutDate}T${formData.expectedCheckoutTime}`),
        pricing: {
          initialRoomAmount: parseFloat(formData.roomAmount) || 0,
          paidAmount: parseFloat(formData.paidAmount) || 0
        },
        specialRequests: formData.specialRequests,
        notes: formData.notes,
        persons: formData.persons,
        paymentMethod: formData.paymentMethod || 'cash'
      };

      const response = await api.put(`/vendor/checkin-management/update-booking/${bookingId}`, requestData);
      
      if (response.data.success) {
        toast.success('Booking updated successfully!');
        setShowEditModal(false);
        
        // Emit custom event to notify parent component to refresh data
        window.dispatchEvent(new CustomEvent('bookingUpdated', { detail: { bookingId } }));
      } else {
        toast.error(response.data.message || 'Failed to update booking');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error(error.response?.data?.message || 'Failed to update booking');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowEditModal(false);
  };

  if (!showEditModal || !bookingId) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-6 border w-full max-w-6xl shadow-2xl rounded-xl bg-white">
        <div className="max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 sticky top-0 bg-white">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              <FiEdit2 className="mr-2 text-blue-600" />
              Edit Booking
            </h3>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="text-xl text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Information Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FiCalendar className="mr-2 text-blue-600" />
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Floor
                  </label>
                  <select
                    value={selectedFloor || ''}
                    onChange={(e) => handleFloorChange(e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">All Floors</option>
                    {floors.map((floor) => (
                      <option key={floor._id} value={floor._id}>
                        {floor.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Room *
                  </label>
                  <select
                    required
                    value={formData.roomId}
                    onChange={(e) => handleInputChange('roomId', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">Select Room</option>
                    {filteredRooms.map((room) => (
                      <option key={room._id} value={room._id}>
                        {room.roomNumber} - {room.floorId?.name} ({room.type})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.checkinDate}
                    onChange={(e) => handleInputChange('checkinDate', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.checkinTime}
                    onChange={(e) => handleInputChange('checkinTime', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Checkout Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.expectedCheckoutDate}
                    onChange={(e) => handleInputChange('expectedCheckoutDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Checkout Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.expectedCheckoutTime}
                    onChange={(e) => handleInputChange('expectedCheckoutTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Room Amount
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.roomAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        setFormData({ ...formData, roomAmount: value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paid Amount
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formData.paidAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        setFormData({ ...formData, paidAmount: value });
                      }
                    }}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={formData.paymentMethod || 'cash'}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FiFileText className="mr-2 text-purple-600" />
                Additional Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requests
                  </label>
                  <textarea
                    value={formData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    rows="3"
                    placeholder="Any special requests from the guest"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    disabled={bookingStatus === 'checked_in'}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    rows="3"
                    placeholder="Additional notes or comments"
                  />
                </div>
              </div>
            </div>

            {/* Guest Details Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FiUser className="mr-2 text-purple-600" />
                Guest Details
              </h4>

              <div className="space-y-6">
                {formData.persons.map((person, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-300">
                      <h5 className="text-base font-semibold text-gray-900">Guest {index + 1}</h5>
                      {formData.persons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePerson(index)}
                          disabled={bookingStatus === 'checked_in'}
                          className={`px-3 py-1 bg-red-100 text-red-600 text-sm rounded-lg hover:bg-red-200 transition-colors ${
                            bookingStatus === 'checked_in' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-4">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Personal Information</h6>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Guest Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={person.name}
                            onChange={(e) => updatePerson(index, 'name', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter full name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Mobile Number
                          </label>
                          <input
                            type="tel"
                            value={person.mobileNumber}
                            onChange={(e) => updatePerson(index, 'mobileNumber', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Mobile number"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={person.email}
                            onChange={(e) => updatePerson(index, 'email', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="email@example.com"
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Identity Information</h6>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Identity Number
                          </label>
                          <input
                            type="text"
                            value={person.identityNumber}
                            onChange={(e) => updatePerson(index, 'identityNumber', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter identity number"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Age
                          </label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={person.age}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*$/.test(value)) {
                                const updatedPersons = [...formData.persons];
                                updatedPersons[index].age = value;
                                setFormData({ ...formData, persons: updatedPersons });
                              }
                            }}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Age"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Gender
                          </label>
                          <select
                            value={person.gender}
                            onChange={(e) => updatePerson(index, 'gender', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          >
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h6 className="text-sm font-medium text-gray-700 mb-2">Address</h6>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Address
                          </label>
                          <textarea
                            value={person.address}
                            onChange={(e) => updatePerson(index, 'address', e.target.value)}
                            disabled={bookingStatus === 'checked_in'}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                              bookingStatus === 'checked_in' ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            rows="4"
                            placeholder="Complete address"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center mt-6">
                <button
                  type="button"
                  onClick={addPerson}
                  disabled={bookingStatus === 'checked_in'}
                  className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center ${
                    bookingStatus === 'checked_in' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <FiEdit2 className="mr-2" />
                  Add Guest
                </button>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 sticky bottom-0 bg-white pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <FiSave className="mr-2" />
                {loading ? 'Updating...' : 'Update Booking'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBookingModal;
